/**
 * CompleteProfilePage - Role Selection Onboarding
 *
 * Two entry paths:
 *  1. New registrants arrive with `?role=<ACCOUNT_ROLE>` (chosen on the Register
 *     page's "I want to join as" step). We DO NOT ask them to pick a role again —
 *     the chosen role is applied automatically as their userType and they are
 *     routed straight to that role's dashboard. This is the fix for the old flow
 *     where the "Please select your role" screen re-appeared after registration.
 *  2. Existing users who land here without a role param (e.g. legacy accounts
 *     with no userType) get the manual selection grid, which now also routes to
 *     the chosen role's dashboard.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { LanguageSwitcher } from '@/components/portal/LanguageSwitcher'
import { PortalBackground } from '@/components/portal/PortalBackground'
import { destinationForUserType, type UserType } from '@/utils/userTypeDestination'
import { User, Building2, ArrowRight, Check, Loader2 } from 'lucide-react'

interface UserTypeOption {
  id: string
  labelKey: string
  descKey: string
  icon: React.ReactNode
  value: UserType
}

const USER_TYPES: UserTypeOption[] = [
  {
    id: 'user',
    labelKey: 'auth.roleUser',
    descKey: 'auth.roleUserDesc',
    icon: <User className="w-8 h-8" />,
    value: 'USER',
  },
  {
    id: 'ngo',
    labelKey: 'auth.roleNgo',
    descKey: 'auth.roleNgoDesc',
    icon: <Building2 className="w-8 h-8" />,
    value: 'NGO',
  },
]

/**
 * Map the account-role chosen on the Register page → the backend userType +
 * the role-specific dashboard to land on. The only registration account role
 * is CITIZEN (Doctor / Health Worker personas were retired; supporter roles
 * never reach this page).
 */
const ROLE_MAP: Record<string, { userType: UserType; destination: string }> = {
  CITIZEN: { userType: 'USER', destination: '/dashboard' },
  USER: { userType: 'USER', destination: '/dashboard' },
}

const CompleteProfilePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, updateProfile, pendingRole, clearPendingRole } = useAuthStore()
  const [searchParams] = useSearchParams()
  // The chosen role arrives either via the URL (?role=…, legacy/direct entry) or
  // via the auth store's pendingRole (set by RegisterPage; preferred because it
  // survives the /register route-guard redirect without a navigate() race).
  const roleParam = searchParams.get('role') || pendingRole
  const mapped = roleParam ? ROLE_MAP[roleParam] : null

  const [selectedType, setSelectedType] = useState<UserType | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // If the automatic apply fails, we fall back to the manual selection UI so the
  // user is never stuck. Once we've fallen back, suppress the auto screen.
  const [autoFailed, setAutoFailed] = useState(false)

  const appliedRef = useRef(false)

  /** PUT the userType to the backend, update the auth store, and route to the
   *  role's dashboard. Returns true on success. */
  const applyUserType = useCallback(
    async (userType: UserType, destination: string): Promise<boolean> => {
      setLoading(true)
      setError(null)
      try {
        const response = await api.put('/users/me/user-type', { userType })
        if (response.data.success) {
          updateProfile({ userType, profileCompleted: true })
          clearPendingRole() // consume the transient role now that it's applied
          navigate(destination, { replace: true })
          return true
        }
        setError(response.data.message || t('auth.failedToUpdateProfile'))
      } catch (err: any) {
        setError(err.response?.data?.message || t('auth.somethingWentWrong'))
      } finally {
        setLoading(false)
      }
      return false
    },
    [navigate, t, updateProfile, clearPendingRole],
  )

  // Path 1: a role was passed from registration → apply it automatically.
  useEffect(() => {
    if (appliedRef.current) return
    if (!mapped) return
    appliedRef.current = true
    ;(async () => {
      const ok = await applyUserType(mapped.userType, mapped.destination)
      if (!ok) setAutoFailed(true)
    })()
  }, [mapped, applyUserType])

  // Path 2: manual selection (existing users without a role param, or auto-fail).
  const handleContinue = async () => {
    if (!selectedType) {
      setError(t('auth.pleaseSelectRole'))
      return
    }
    await applyUserType(selectedType, destinationForUserType(selectedType))
  }

  const showAutoScreen = mapped && !autoFailed

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Shared site background (decorative, behind content) */}
      <PortalBackground />
      {/* Language switcher — prominent, top-right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="relative z-10 w-full max-w-2xl">
        {showAutoScreen ? (
          /* Path 1: applying the role chosen at registration — no re-select. */
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg mb-4">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.settingUpProfile')}</h1>
            <p className="text-gray-600 max-w-md mx-auto">
              {error ? error : t('auth.redirectingToYourDashboard')}
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.completeProfileTitle')}</h1>
              <p className="text-gray-600 max-w-md mx-auto">
                {t('auth.completeProfileSubtitle')}
              </p>
            </div>

            {/* Role Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {USER_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.value)}
                  className={`relative p-6 rounded-2xl border-2 transition-all duration-200 text-left group ${
                    selectedType === type.value
                      ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-500/10'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  {/* Selected Check */}
                  {selectedType === type.value && (
                    <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}

                  {/* Icon */}
                  <div
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3 transition-colors ${
                      selectedType === type.value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                    }`}
                  >
                    {type.icon}
                  </div>

                  {/* Label & Description */}
                  <h3 className="font-semibold text-gray-900 mb-1">{t(type.labelKey)}</h3>
                  <p className="text-sm text-gray-500">{t(type.descKey)}</p>
                </button>
              ))}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-center text-sm">
                {error}
              </div>
            )}

            {/* Continue Button */}
            <button
              onClick={handleContinue}
              disabled={!selectedType || loading}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all ${
                selectedType && !loading
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/25'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t('auth.updating')}</span>
                </>
              ) : (
                <>
                  <span>{t('auth.continueBtn')}</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {/* Skip for now (only for existing users) */}
            {user?.profileCompleted === false && user?.userType === undefined && (
              <button
                onClick={() => navigate('/feed')}
                className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700 text-sm"
              >
                {t('auth.skipForNow')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CompleteProfilePage